import { atom } from "recoil";

import {
  IReportCriteriaFiltersSchemaValues,
  IReportTemplateFilters,
} from "@ryde/shared";

import { DASHBOARD_DEFAULT_CARDS } from "./dynamic-dashboard.default-layout";
import { IDashboardCard } from "./dynamic-dashboard.models";

/**
 * The dashboard's working set of cards. Seeded with the default layout (today's
 * dashboard); drag-reorder, the card editor, and applying a saved view all
 * write back here. Saved views themselves are persisted via the real ViewApi
 * (see use-dashboard-views), not here.
 */
export const dashboardCardsAtom = atom<IDashboardCard[]>({
  key: "dashboardCardsAtom",
  default: DASHBOARD_DEFAULT_CARDS,
});

export interface IDashboardEditorState {
  isOpen: boolean;
  /** The card being edited, or null when creating a new card. */
  cardId: string | null;
}

/** Open/closed state + target of the card edit panel. */
export const dashboardEditorAtom = atom<IDashboardEditorState>({
  key: "dashboardEditorAtom",
  default: { isOpen: false, cardId: null },
});

/**
 * The APPLIED dashboard filters (raw `{ templateKey: value }`), broadcast to
 * every card's report fetch on "Apply". Cards read this and each report applies
 * only the filter keys it declares (the engine silently ignores the rest), so a
 * single bar narrows heterogeneous cards; permissions stay enforced server-side.
 * Live editing lives in the reports' own `reportFiltersWithValuesAF` (keyed by
 * the dashboard's sentinel report id); Apply snapshots it into here.
 */
export const dashboardAppliedFiltersAtom =
  atom<IReportCriteriaFiltersSchemaValues>({
    key: "dashboardAppliedFiltersAtom",
    default: {},
  });
export const dashboardFilterSchemaByReportAtom = atom<
  Record<number, IReportTemplateFilters>
>({
  key: "dashboardFilterSchemaByReportAtom",
  default: {},
});

/**
 * Whether the default saved view has already been auto-applied this session.
 * A Recoil atom (not a component ref) so it survives the view panel
 * unmounting/remounting on route changes — otherwise returning to the dashboard
 * would re-apply the default view and clobber the user's active selection /
 * unsaved edits. Resets only on a true page reload (a fresh cold load).
 */
export const dashboardDefaultViewAppliedAtom = atom<boolean>({
  key: "dashboardDefaultViewAppliedAtom",
  default: false,
});
